/**
 * Guide content configuration for the onboarding checklist step panels.
 *
 * Only structure lives here (icon names, screenshot paths, deep links). Every string
 * comes from i18n under `onboardingChecklist.steps.<id>.guide.*`.
 *
 * Screenshots are optional: the panel renders text-only when `shot` is missing or the
 * image fails to load.
 */

import AddIcon from '@mui/icons-material/Add';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContactlessIcon from '@mui/icons-material/Contactless';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LinkIcon from '@mui/icons-material/Link';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MailIcon from '@mui/icons-material/Mail';
import PersonIcon from '@mui/icons-material/Person';
import PinIcon from '@mui/icons-material/Pin';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Captured from the local dev stack with e2e/scripts/capture-onboarding-shots.js.
const SHOT_BASE = '/img/onboarding';

/**
 * ways: [{ icon }] — the copy for way N is `...guide.ways.N.title` / `.desc`.
 * cta:  { icon, action } — `action` is interpreted by OnboardingChecklistCard.
 */
export const STEP_GUIDES = {
  workorder: {
    ways: [{ icon: PinIcon }, { icon: PersonIcon }, { icon: EditNoteIcon }],
    shot: `${SHOT_BASE}/workorder.png`,
    cta: { icon: AddIcon, action: 'createWorkOrder' },
  },
  invoice: {
    ways: [{ icon: TaskAltIcon }, { icon: FactCheckIcon }, { icon: SendIcon }],
    shot: `${SHOT_BASE}/invoice.png`,
    cta: { icon: ReceiptLongIcon, action: 'openInvoiceables' },
  },
  einvoice: {
    // Activate in settings -> sign with Visma Sign -> sending picks the route.
    ways: [{ icon: AppRegistrationIcon }, { icon: VerifiedUserIcon }, { icon: AltRouteIcon }],
    shot: `${SHOT_BASE}/einvoice.png`,
    cta: { icon: SettingsIcon, action: 'navigate', to: '/settings?tab=verkkolaskutus' },
  },
  suppliers: {
    ways: [{ icon: VpnKeyIcon }, { icon: Inventory2Icon }, { icon: LocalShippingIcon }],
    shot: `${SHOT_BASE}/suppliers.png`,
    cta: { icon: SettingsIcon, action: 'navigate', to: '/settings?tab=integrations' },
  },
  terminal: {
    ways: [{ icon: ShoppingCartCheckoutIcon }, { icon: LinkIcon }, { icon: ContactlessIcon }],
    shot: `${SHOT_BASE}/terminal.png`,
    cta: { icon: PointOfSaleIcon, action: 'requestTerminal' },
  },
  staff: {
    ways: [{ icon: MailIcon }, { icon: LockOpenIcon }, { icon: AssignmentIndIcon }],
    shot: `${SHOT_BASE}/staff.png`,
    cta: { icon: GroupAddIcon, action: 'navigate', to: '/settings?tab=permissions' },
  },
  booking: {
    ways: [{ icon: DesignServicesIcon }, { icon: EventAvailableIcon }, { icon: ShareIcon }],
    shot: `${SHOT_BASE}/booking.png`,
    cta: { icon: SettingsIcon, action: 'navigate', to: '/settings?tab=features&subtab=booking' },
  },
};

/** Parts has its own panel (work order picker), but reuses the "ways" layout. */
export const PARTS_WAYS = [{ icon: AttachFileIcon }, { icon: SearchIcon }, { icon: EditNoteIcon }];

export const PARTS_SHOT = `${SHOT_BASE}/parts.png`;

export default STEP_GUIDES;
